import { useState } from 'react';

import { api } from '../api/client';
import type { SavedChart, SavedWidget } from '../types';
import { ChartRenderer } from './ChartRenderer';
import { Empty } from '../charts/states';
import { embedParams } from '../embedParams';

/**
 * Widgets are single charts sized to the platform's Aspect Ratio dropdown.
 *
 * The ratio is a hard constraint on the platform's grid card, and the embed is
 * scaled to fit — so the widget must not scroll. `none` is deliberately not
 * offered: it lets the height float, which is what produces a scrollbar.
 */
const ASPECT_RATIOS = [
  { value: '2/1', hint: 'KPI tiles' },
  { value: '16/9', hint: 'Trends and maps' },
  { value: '4/3', hint: 'Bar charts' },
  { value: '1/1', hint: 'Donuts' },
  { value: '3/2', hint: 'Hero cards' },
  { value: '3/4', hint: 'Leaderboards and tables' },
  { value: '2/3', hint: 'Short leaderboards' },
  { value: '4/5', hint: 'Tall cards' },
  { value: '5/4', hint: 'Wide cards' },
  { value: '9/16', hint: 'Portrait' },
  { value: '21/9', hint: 'Ultra-wide strips' },
  { value: '9/21', hint: 'Tall strips' },
  { value: '1/2', hint: 'Tall narrow' },
];

interface Props {
  charts: SavedChart[];
  editing?: SavedWidget | null;
  onSaved: (widget: SavedWidget) => void;
  onCancel: () => void;
}

export const WidgetBuilder = ({ charts, editing, onSaved, onCancel }: Props) => {
  const [name, setName] = useState(editing?.name ?? '');
  const [chartId, setChartId] = useState(editing?.chartId ?? charts[0]?.id ?? '');
  const [aspectRatio, setAspectRatio] = useState(editing?.aspectRatio ?? '16/9');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const chart = charts.find((c) => c.id === chartId);

  const save = async () => {
    if (!name.trim() || !chartId) return;
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await api.saveWidget({ id: editing?.id, name: name.trim(), chartId, aspectRatio }),
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="studio">
      <div className="studio__panel">
        <h2>{editing ? 'Edit widget' : 'New widget'}</h2>

        <label>
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Total downloads" />
        </label>

        <label>
          <span>Chart</span>
          <select value={chartId} onChange={(e) => setChartId(e.target.value)}>
            {charts.length === 0 && <option value="">Create a chart first</option>}
            {charts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Aspect ratio</span>
          <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)}>
            {ASPECT_RATIOS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.value} — {r.hint}
              </option>
            ))}
          </select>
        </label>
        <p className="hint">
          Set the same ratio on the platform widget so the card and the embed agree.
        </p>

        {error && <p className="error">{error}</p>}

        <div className="studio__actions">
          <button onClick={onCancel}>Cancel</button>
          <button className="primary" onClick={save} disabled={busy || !name.trim() || !chartId}>
            {editing ? 'Save changes' : 'Create widget'}
          </button>
        </div>
        {!editing && (
          <p className="hint reminder">
            After you create it, copy the new widget id and its chart's description into the{' '}
            <a href="/help#registry" target="_self">ADO Chart Registry</a>. The platform points
            widget content items at <code>/w/&lt;widgetId&gt;</code>, so the registry is where the
            team looks up which chart a widget carries and what it says.
          </p>
        )}
      </div>

      <div className="studio__preview">
        <header>Preview at {aspectRatio}</header>
        <div className="widget-frame" style={{ aspectRatio: aspectRatio.replace('/', ' / ') }}>
          {/* Same app filter the widget will actually render with at /w/<id>
              (see WidgetView), so the preview is not a kinder environment than
              the embed — a chart that needs app_id fails here too, and fails
              the same way, rather than only after the widget is created. */}
          {chart ? (
            <ChartRenderer chart={chart} overrideParams={embedParams()} chrome={false} />
          ) : (
            <Empty message="Pick a chart" />
          )}
        </div>
      </div>
    </div>
  );
};
